import { isPlatformBrowser } from "@angular/common";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { inject, Injectable, PLATFORM_ID, signal } from "@angular/core";
import { AddCartItemDTO, Cart } from "@models/cart";
import { Order, OrderRequestTimeframe } from "@models/order";
import { Page } from "@models/page";
import { PaginatorState } from "primeng/paginator";
import { catchError, Observable, of, tap } from "rxjs";
import { environment } from "../../environments/environment";
import { LocalStorageService } from "./local-storage.service";

export interface FreePurchaseResponse {
  success: boolean;
  message?: string;
  order_id?: number;
  payment_status?: string;
  total_amount?: number;
  sales_count?: number;
  error?: string;
}

@Injectable({
  providedIn: "root",
})
export class CartService {
  private apiUrl = environment.API_BACKEND;

  readonly cartItemCount = signal(0);

  constructor(
    private http: HttpClient,
    private localStorageService: LocalStorageService,
  ) {}

  platformId = inject(PLATFORM_ID);

  private getToken(): string | null {
    if (isPlatformBrowser(this.platformId)) {
      return this.localStorageService.getItem("auth-token");
    }
    return null;
  }

  private getHttpHeaders(): HttpHeaders {
    const token = this.getToken();
    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });
  }

  getCart(): Observable<Cart> {
    return this.http
      .get<Cart>(`${this.apiUrl}/cart`, { headers: this.getHttpHeaders() })
      .pipe(tap((cart) => this.updateCartItemCount(cart)));
  }

  refreshCartCount(): void {
    this.getCart()
      .pipe(catchError(() => of(null)))
      .subscribe();
  }

  resetCartCount(): void {
    this.cartItemCount.set(0);
  }

  private updateCartItemCount(cart?: Cart | null): void {
    if (!cart?.cart_items) {
      this.cartItemCount.set(0);
      return;
    }
    const total = cart.cart_items.reduce(
      (sum, item) => sum + Number(item.quantity ?? 0),
      0,
    );
    this.cartItemCount.set(total);
  }

  /**
   * Adiciona um ingresso ao carrinho. Com `replace = true`, o backend esvazia o carrinho
   * (devolvendo o estoque) antes de adicionar — usado quando o usuário confirma trocar de
   * evento. Sem replace, tentar adicionar ingresso de outro evento retorna 409 com
   * `{ code: "different_event_in_cart" }`.
   */
  addItem(cartItem: AddCartItemDTO, replace = false): Observable<any> {
    return this.http.put(
      `${this.apiUrl}/cart/add`,
      { cart_item: cartItem, replace },
      { headers: this.getHttpHeaders() },
    );
  }

  removeItem(itemId: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/cart/remove/${itemId}`, {
      headers: this.getHttpHeaders(),
    });
  }

  purchaseOrder(requestBody: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/cart/purchase`, requestBody, {
      headers: this.getHttpHeaders(),
    });
  }

  /**
   * Finaliza a compra de um carrinho gratuito (total R$ 0). Emite os ingressos
   * como cortesia, sem passar pelo gateway de pagamento.
   */
  purchaseFreeOrder(): Observable<FreePurchaseResponse> {
    return this.http.post<FreePurchaseResponse>(
      `${this.apiUrl}/cart/purchase_free`,
      {},
      { headers: this.getHttpHeaders() },
    );
  }

  getMyOrders(
    timeframe: OrderRequestTimeframe,
    page?: PaginatorState,
  ): Observable<Page<Order>> {
    const httpParams = new HttpParams()
      .set("timeframe", timeframe)
      .set("page", page?.page ?? 0)
      .set("per_page", page?.rows ?? 10);

    return this.http
      .get<Page<Order>>(`${this.apiUrl}/my_orders`, {
        headers: this.getHttpHeaders(),
        params: httpParams,
      })
      .pipe(
        catchError(() =>
          of({
            data: [],
            meta: {
              total_count: 0,
              total_pages: 0,
              current_page: 0,
              page_size: 10,
            },
          } as Page<Order>),
        ),
      );
  }
}
